/**
 * Date/Time Utility Functions
 * Handles timezone conversions for session times
 * IMPORTANT: Times are stored as pseudo-UTC (local time with Z suffix)
 * All display functions should use timeZone: 'UTC' to avoid date shifting
 * EXCEPTION: actualStartTime and actualEndTime are stored as real UTC
 */

// Project-wide date format: "10 AUG 2026" (DD MMM-uppercase YYYY).
// Helper that builds the standardized "10 AUG 2026" string for a given
// Date in a given time zone. Used by every date/datetime formatter below
// so the format is consistent everywhere.
const formatDateUppercase = (date, timeZone) => {
  const fmt = new Intl.DateTimeFormat('en-GB', {
    ...(timeZone ? { timeZone } : {}),
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
  // en-GB short month is "Aug"; uppercase the whole string is safe (only
  // letters in the month survive the operation).
  return fmt.format(date).toUpperCase();
};

/**
 * Format a date/time for display — "10 AUG 2026, 3:30 PM" format.
 * Uses UTC to display stored pseudo-UTC times correctly.
 * @param {string|Date} dateTime - Date/time from server (stored as pseudo-UTC)
 * @returns {string} Formatted date/time string
 */
export const formatLocalDateTime = (dateTime) => {
  if (!dateTime) return '';

  const date = new Date(dateTime);
  if (isNaN(date.getTime())) return '';

  const datePart = formatDateUppercase(date, 'UTC');
  const timePart = date.toLocaleTimeString('en-US', {
    timeZone: 'UTC',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return `${datePart}, ${timePart}`;
};

/**
 * Format a real UTC timestamp for display in user's local timezone — same
 * "10 AUG 2026, 3:30 PM" format as formatLocalDateTime.
 * Use this for actualStartTime, actualEndTime, and other real UTC timestamps.
 * @param {string|Date} dateTime - Real UTC timestamp from server
 * @returns {string} Formatted date/time string in local timezone
 */
export const formatActualDateTime = (dateTime) => {
  if (!dateTime) return '';

  const date = new Date(dateTime);
  if (isNaN(date.getTime())) return '';

  const datePart = formatDateUppercase(date);
  const timePart = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return `${datePart}, ${timePart}`;
};

/**
 * Format just the time portion
 * Uses UTC to display stored pseudo-UTC times correctly
 * @param {string|Date} dateTime - Date/time from server (stored as pseudo-UTC)
 * @returns {string} Formatted time (e.g., "3:30 PM")
 */
export const formatLocalTime = (dateTime) => {
  if (!dateTime) return '';

  const date = new Date(dateTime);
  if (isNaN(date.getTime())) return '';

  return date.toLocaleTimeString('en-US', {
    timeZone: 'UTC',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

/**
 * Format just the time portion for real UTC timestamps
 * Displays in user's local timezone (for actualStartTime, launchedAt, etc.)
 * @param {string|Date} dateTime - Real UTC timestamp from server
 * @returns {string} Formatted time in local timezone (e.g., "3:30 PM")
 */
export const formatActualTime = (dateTime) => {
  if (!dateTime) return '';

  const date = new Date(dateTime);
  if (isNaN(date.getTime())) return '';

  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

/**
 * Format just the date portion — "10 AUG 2026" format.
 * Uses UTC to display stored pseudo-UTC dates correctly.
 * @param {string|Date} dateTime - Date/time from server (stored as pseudo-UTC)
 * @returns {string} Formatted date (e.g., "10 AUG 2026")
 */
/**
 * Format a TRUE INSTANT in the viewer's own timezone — "29 OCT 2026".
 *
 * Distinct from formatLocalDate, which renders in UTC because session dates are
 * stored pseudo-UTC (local wall-clock placed in a UTC field). Reflection
 * availableAt is a genuine instant, so rendering it as UTC shows the wrong day
 * whenever the instant sits on the other side of midnight UTC: an unlock at
 * 29 Oct 00:00 IST is 28 Oct 18:30 UTC, and printed "28 OCT" — a day early.
 *
 * @param {string|Date} instant - a real UTC instant from the server
 * @returns {string} e.g. "29 OCT 2026"
 */
export const formatInstantDate = (instant) => {
  if (!instant) return '';
  const date = new Date(instant);
  if (isNaN(date.getTime())) return '';
  return formatDateUppercase(date); // no timeZone → viewer's own zone
};

export const formatLocalDate = (dateTime) => {
  if (!dateTime) return '';

  const date = new Date(dateTime);
  if (isNaN(date.getTime())) return '';

  return formatDateUppercase(date, 'UTC');
};

/**
 * Format date and time range for session display
 * @param {string|Date} startTime - Start time from server
 * @param {string|Date} endTime - End time from server
 * @returns {string} Formatted range (e.g., "8:30 PM - 9:30 PM")
 */
export const formatTimeRange = (startTime, endTime) => {
  const start = formatLocalTime(startTime);
  const end = formatLocalTime(endTime);

  if (!start && !end) return '';
  if (!end) return start;

  return `${start} - ${end}`;
};

/**
 * Format full session date and time
 * @param {string|Date} date - Session date
 * @param {string|Date} startTime - Start time
 * @param {string|Date} endTime - End time
 * @returns {string} Formatted string (e.g., "Dec 22, 2025 • 8:30 PM - 9:30 PM")
 */
export const formatSessionDateTime = (date, startTime, endTime) => {
  const dateStr = formatLocalDate(date || startTime);
  const timeRange = formatTimeRange(startTime, endTime);

  if (!dateStr && !timeRange) return '';
  if (!timeRange) return dateStr;
  if (!dateStr) return timeRange;

  return `${dateStr} • ${timeRange}`;
};

/**
 * Convert local time input to UTC for sending to server
 * @param {string} dateStr - Date string (YYYY-MM-DD)
 * @param {string} timeStr - Time string (HH:MM in 24-hour format)
 * @returns {Date} UTC Date object
 */
export const localToUTC = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return null;

  // Create a local date from the date and time strings
  const localDate = new Date(`${dateStr}T${timeStr}:00`);
  return localDate;
};

/**
 * Get relative time string (e.g., "in 2 hours", "yesterday")
 * @param {string|Date} dateTime - Date/time to compare
 * @returns {string} Relative time string
 */
export const getRelativeTime = (dateTime) => {
  if (!dateTime) return '';

  const date = new Date(dateTime);
  if (isNaN(date.getTime())) return '';

  const now = new Date();
  const diffMs = date - now;
  const diffMins = Math.round(diffMs / 60000);
  const diffHours = Math.round(diffMs / 3600000);
  const diffDays = Math.round(diffMs / 86400000);

  if (Math.abs(diffMins) < 60) {
    if (diffMins >= 0) return `in ${diffMins} min`;
    return `${Math.abs(diffMins)} min ago`;
  }

  if (Math.abs(diffHours) < 24) {
    if (diffHours >= 0) return `in ${diffHours} hours`;
    return `${Math.abs(diffHours)} hours ago`;
  }

  if (diffDays >= 0) return `in ${diffDays} days`;
  return `${Math.abs(diffDays)} days ago`;
};

export default {
  formatLocalDateTime,
  formatActualDateTime,
  formatLocalTime,
  formatActualTime,
  formatLocalDate,
  formatTimeRange,
  formatSessionDateTime,
  localToUTC,
  getRelativeTime,
};
